import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaUser, FaShieldAlt, FaBell, FaKey, FaCreditCard, FaUsers, FaCog, FaCloud, FaPalette, FaRobot, FaPlug
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import ProfileTab from "./ProfileTab";
import SecurityTab from "./SecurityTab";
import NotificationsTab from "./NotificationsTab";
import ApiKeysTab from "./ApiKeysTab";
import BillingTab from "./BillingTab";
import TeamTab from "./TeamTab";
import GeneralTab from "./GeneralTab";
import StorageTab from "./StorageTab";
import AppearanceTab from "./AppearanceTab";
import AISettingsTab from "./AISettingsTab";
import WebhooksTab from "./WebhooksTab";

const allTabs = [
  { key: "profile", label: "Profile", icon: FaUser },
  { key: "security", label: "Security", icon: FaShieldAlt },
  { key: "appearance", label: "Appearance", icon: FaPalette },
  { key: "notifications", label: "Notifications", icon: FaBell },
  { key: "api-keys", label: "API Keys", icon: FaKey },
  { key: "webhooks", label: "Webhooks", icon: FaPlug },
  { key: "ai", label: "AI Settings", icon: FaRobot },
  { key: "billing", label: "Billing", icon: FaCreditCard, adminOnly: true },
  { key: "team", label: "Team", icon: FaUsers },
  { key: "storage", label: "Cloud Storage", icon: FaCloud },
  { key: "general", label: "General", icon: FaCog }
];

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { teamRole } = useAuth();
  const isTeamMember = teamRole === "member";
  const tabs = isTeamMember ? allTabs.filter(t => !t.adminOnly) : allTabs;
  const hash = location.hash.replace("#", "") || "profile";

  const renderTab = () => {
    switch (hash) {
      case "profile": return <ProfileTab />;
      case "security": return <SecurityTab />;
      case "appearance": return <AppearanceTab />;
      case "notifications": return <NotificationsTab />;
      case "api-keys": return <ApiKeysTab />;
      case "webhooks": return <WebhooksTab />;
      case "ai": return <AISettingsTab />;
      case "billing": return <BillingTab />;
      case "team": return <TeamTab />;
      case "storage": return <StorageTab />;
      case "general": return <GeneralTab />;
      default: return <ProfileTab />;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full">
      {/* Mobile: horizontal scrollable tabs */}
      <nav className="md:hidden overflow-x-auto border-b border-surface-border bg-surface-card px-3 py-2 flex gap-1 flex-shrink-0">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => navigate(`#${tab.key}`)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              hash === tab.key ? 'bg-brand text-white' : 'bg-surface-bg text-ink-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Desktop: vertical sidebar */}
      <nav className="hidden md:block w-[220px] flex-shrink-0 bg-surface-card border-r border-surface-border p-3 space-y-1">
        {tabs.map((t) => {
          const isActive = hash === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => navigate(`#${t.key}`)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                isActive
                  ? "bg-brand-50 text-brand-700 border border-brand/30"
                  : "text-ink-secondary hover:bg-surface-bg"
              }`}
            >
              <Icon size={14} className={isActive ? "text-brand" : "text-ink-muted"} />
              {t.label}
            </button>
          );
        })}
      </nav>

      {/* Right content */}
      <div className="flex-1 p-6 overflow-y-auto">{renderTab()}</div>
    </div>
  );
}
